<template>
  <div class="screen flex flex-wrap overflow-hidden rounded-md shadow">
    <aside class="max-w-xs w-full flex flex-col p-4 bg-white">
      <div class="mb-5 pt-1">
        <h4 class="title mb-3 font-semibold text-sm uppercase">Camera</h4>
        <div class="flex items-center space-x-3">
          <feather-icon class="icon" icon="MapPinIcon" />
          <span class="font-semibold">{{ detail.siteName }}</span>
        </div>
        <div class="flex items-center space-x-3">
          <feather-icon class="icon" icon="VideoIcon" />
          <span class="font-semibold">{{ camera.cameraName }}</span>
        </div>
      </div>
      <div class="mb-5">
        <h4 class="title mb-3 font-semibold text-sm uppercase">Detail</h4>
        <div class="mb-3">
          <span class="title uppercase tracking-wide text-xs">Name</span>
          <p class="font-semibold">{{ camera.name }}</p>
        </div>
        <div class="mb-3">
          <span class="title uppercase tracking-wide text-xs">Deskripsi</span>
          <p class="font-semibold">{{ camera.description || "-" }}</p>
        </div>
        <div class="mb-3">
          <span class="title uppercase tracking-wide text-xs">Start Time</span>
          <p v-if="camera.startTime" class="font-semibold">
            {{ prettyDate(camera.startTime) }}
          </p>
        </div>
        <div class="mb-3">
          <span class="title uppercase tracking-wide text-xs">End Time</span>
          <p v-if="camera.endTime" class="font-semibold">
            {{ prettyDate(camera.endTime) }}
          </p>
        </div>
        <div class="mb-3">
          <span class="title uppercase tracking-wide text-xs">Duration</span>
          <p class="font-semibold">{{ duration }}</p>
        </div>
      </div>
    </aside>
    <div class="flex-1 relative">
      <Player
        v-if="isVisibleVideo"
        :title="camera.cameraName"
        :isPTZ="false"
        :url="camera.streamingURL"
        :options="videoOptions"
        :resize="true"
        :time-start="startTime"
        :time-end="endTime"
        :rewind="30"
        is-muted
        is-mic-muted
        show-settings
        hide-bookmark
        @skip="onSkip"
        @action:export="onExport"
        @action:snapshot="onSnapshot"
        @timeline:click="onSkip"
        @quality:change="onQualityChange"
        @action:ptzup="movePTZ(item,'up')"
        @action:ptzleft="movePTZ(item,'left')"
        @action:ptzright="movePTZ(item,'right')"
        @action:ptzdown="movePTZ(item,'down')"
      />
      <!-- Snapshot Animation -->
      <transition name="snapshot" mode="out-in">
        <div v-if="isSnapshot" class="snapshot-screen">
          <span class="snapshot-icon">
            <vs-icon icon="filter_center_focus" size="64px" color="black" />
          </span>
        </div>
      </transition>
    </div>
    <!-- Download Bar -->
    <DownloadBar
      :is-downloading="isDownloading"
      :progress="percentageDownload"
      @close="isDownloading = false"
    />
  </div>
</template>

<script>
import api from "@/api";
import Mixins from "@/composables/mixins";
import { toDuration, getTime, GMTToLocalStringTime } from "@/composables";
import { videoOptions } from "@/views/views/states";
import Player from "@/components/player/VideoPlayer.vue";
import DownloadBar from "@/components/player/DownloadBar.vue";
export default {
  name: "ShowBookmark",
  components: {
    Player,
    DownloadBar,
  },
  mixins: [Mixins],
  data: () => ({
    detail: {},
    camera: {},
    isSnapshot: false,
    isVisibleVideo: false,
    videoOptions,
    quality: "low",
    percentageDownload: 0,
  }),
  computed: {
    duration() {
      if (this.detail && this.camera) {
        const start = getTime(this.camera.startTime);
        const end = getTime(this.camera.endTime);
        return toDuration({
          startTime: start,
          endTime: end,
        });
      }
    },
    startTime() {
      return new Date(this.camera.startTime).getTime();
    },
    endTime() {
      return new Date(this.camera.endTime).getTime();
    },
  },
  async mounted() {
    await this.getDetail();
  },
  methods: {
    prettyDate(date) {
      const format = new Date(date);
      return new Intl.DateTimeFormat("id-ID", {
        dateStyle: "short",
        timeStyle: "long",
      }).format(format);
    },
    async getDetail() {
      try {
        this.$vs.loading();
        const id = this.$route.params.id;
        const { data } = await api.bookmarkDetail({ bookmarkId: id });
        this.detail = data.data[0];
        this.camera = this.detail.cameraBookmark[0];
        this.$vs.loading.close();
        this.isVisibleVideo = true;
      } catch (error) {
        this.$vs.loading.close();
      }
    },

    // take the snapshot
    async onSnapshot() {
      try {
        this.isSnapshot = true;

        const { data } = await api.viewSnapshot({
          cameraId: this.camera.cameraId,
          startTime: this.camera.startTime,
        });

        const streamingURL = data.data[0].streamingURL;
        const cameraName = `${
          this.camera.cameraName
        }-${new Date().toLocaleString("ID")}`;

        this.isSnapshot = false;

        await this.downloadFile(streamingURL, "image/jpg", cameraName);
      } catch (error) {
        throw new Error(error.response.data.message);
      }
    },
    async onSkip(duration) {
      try {
        const startTime = GMTToLocalStringTime(duration);
        const { data } = await api.viewSkipVideo({
          cameraId: this.camera.cameraId,
          startTime: startTime,
        });

        this.$set(this.camera, "streamingURL", data.data[0].streamingURL);
        this.$set(this.camera, "startTime", duration);
      } catch (error) {
        throw new Error(error);
      }
    },
    async onQualityChange(quality) {
      try {
        this.quality = quality;
        const { data } = await api.viewSkipVideo({
          cameraId: this.camera.cameraId,
          quality: quality,
        });
        this.$set(this.camera, "streamingURL", data.data[0].streamingURL);
      } catch (error) {
        throw new Error(error);
      }
    },
    async onExport() {
      try {
        const streamingURL = this.camera.streamingURL;
        const fileName = `BOOKMARK-${this.camera.cameraName}-${this.prettyDate(
          this.camera.startTime
        )}-${this.duration}`;

        await this.downloadFile(streamingURL, "video/mp4", fileName);
      } catch (error) {
        throw new Error(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.screen {
  min-height: 78vh;
}
.icon {
  @apply w-5;
  color: #1372ac;
}
.title {
  color: #777;
  font-weight: 500;
}
</style>
<style lang="scss" scoped>
@import "@/assets/scss/player/views.scss";
</style>
